import React, { useEffect, useState} from 'react';
import { apiService } from '../../../components/Config/api.service';
import { FaPlus, FaArrowLeft, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { ClipLoader } from 'react-spinners';
import { notify } from '../../../components/Utils/Notifications';
import { FiTrash2 } from 'react-icons/fi';
import ActionDropdown from '../../../components/Utils/ActionDropdown';
import EmployeeDeductionRow from './EmployeesDeduction';
import Modal from '../../../components/Utils/ModalShow';
import AmountInput from "../../../components/Utils/AmountInput";

const dateUtils = {
    getCurrentMonth: () => {
        const date = new Date();
        return `${date.getMonth() + 1}/${date.getFullYear()}`;
    },

    getNextYearMonth: () => {
        const date = new Date();
        return `${date.getMonth() + 1}/${date.getFullYear() + 1}`;
    }
};

const DeductionForm = ({ id, closeEdit }) => {
    const [loading, setLoading] = useState(false);
    const [employeeLoans, setEmployeeLoans] = useState([]);
    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
    const [isAccountSectionExpanded, setIsAccountSectionExpanded] = useState(false);
    const [isEmployeeSectionExpanded, setIsEmployeeSectionExpanded] = useState(true);
    const [formData, setFormData] = useState({
        libelle: '',
        montant: '',
        description: '',
        account_debit: '',
        account_credit: '',
        not_computed_in_net: false,
        isActive: true
    });
    const [validationErrors, setValidationErrors] = useState({});
    const formatMonthYear = (value) => {
        if (!value) return '';
        const [month, year] = value.split('/');
        if (!month || !year) return value;
        const paddedMonth = month.padStart(2, '0');
        return `${paddedMonth}/${year}`;
    };
    useEffect(() => {
        const loadDeductionData = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const response = await apiService.getElementById('/loan', id);
                const loan = response.data.loan;
                setFormData({
                    libelle: loan.name || '',
                    montant: loan.amount || '',
                    description: loan.description || '',
                });
                if (loan.employees_loans && loan.employees_loans.length > 0) {
                    setEmployeeLoans(loan.employees_loans.map(emp => ({
                        employee_select: { value: emp.employee_id, label: emp.employee_name || '' },
                        amount: emp.amount,
                        dateStart: emp.date_start,
                        dateEnd: emp.date_end
                    })));
                }
            } catch (error) {
                console.error('Error loading deduction data:', error);
                notify('Erreur lors de la récupération de la déduction!', '', 3);
            } finally {
                setLoading(false);
            }
        };
        loadDeductionData();
    }, [id]);

    const handleFormChange = (e) => {
        const { id, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: type === 'checkbox' ? checked : value
        }));
        if (validationErrors[id]) {
            setValidationErrors(prev => ({ ...prev, [id]: null }));
        }
    };

    const handleMontantChange = (newValue) => {
        setFormData(prev => ({ ...prev, montant: newValue }));
        if (validationErrors.montant) {
            setValidationErrors(prev => ({ ...prev, montant: null }));
        }
    };

    const addEmployeeLoan = () => {
        setEmployeeLoans([...employeeLoans, {
            employee_id: null,
            employee_name: '',
            amount: formData.montant,
            dateStart: dateUtils.getCurrentMonth(),
            dateEnd: dateUtils.getNextYearMonth()
        }]);
    };

    const removeEmployeeLoan = (index) => {
        setEmployeeLoans(employeeLoans.filter((_, i) => i !== index));
    };

    const handleEmployeeLoanChange = (index, field, value) => {
        console.log("Employee Loan Change:", index, field, value);
        const updatedLoans = [...employeeLoans];
        updatedLoans[index][field] = value;
        console.log("Updated Loans:", updatedLoans);
        setEmployeeLoans(updatedLoans);
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.libelle.trim()) {
            errors.libelle = "Le libellé est requis";
        }
        if (!formData.montant) {
            errors.montant = "Le montant est requis";
        } else if (isNaN(parseFloat(formData.montant)) || parseFloat(formData.montant) <= 0) {
            errors.montant = "Le montant doit être un nombre positif";
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            notify("Veuillez corriger les erreurs dans le formulaire.", '', 3);
            return;
        }

        const deductionData = {
            name: formData.libelle,
            amount: parseFloat(formData.montant),
            description: formData.description,
            employees_loans: employeeLoans.map(loan => ({
                employee_id: loan.employee_select ? loan.employee_select.value : null,
                employee_name: loan.employee_select ? loan.employee_select.label : '',
                amount: parseFloat(loan.amount || formData.montant),
                date_start: formatMonthYear(loan.dateStart),
                date_end: formatMonthYear(loan.dateEnd)
            }))
        };

        console.log("Deduction Data:", deductionData);

        setLoading(true);
        try {
            if (id) {
                await apiService.updateElementById('/loan', id, deductionData);
                notify('Mise à jour réussie !', '', 2);
            } else {
                await apiService.saveElement('/loan', deductionData);
                notify('Création réussie !', '', 2);
            }
            closeEdit();
        } catch (error) {
            console.error('Error submitting form:', error);
            notify(`Erreur lors de ${id ? 'la modification' : "l'ajout"} de la déduction.`, '', 3);
        } finally {
            setLoading(false);
        }
    };

    const handleArchive = async () => {
        setIsArchiveModalOpen(false);
        setLoading(true);
        try {
            const updatedFormData = {
                active: false,
            };
            await apiService.updateElementById(`/loan`, id, updatedFormData);
            notify('Archivage réussi', '', 2);
            closeEdit();
        } catch (error) {
            console.error('Error Archivage deduction:', error);
            notify('Erreur lors Archivage de la déduction.', '', 3);
        } finally {
            setLoading(false);
        }
    };

    const dropdownActions = [
        {
            label: 'Archiver',
            onClick: () => setIsArchiveModalOpen(true),
            disabled: loading || !id,
            color: 'text-red-500',
            icon: <FiTrash2 size={16} />
        }
    ]

    return (
    <>
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className='flex justify-between items-center mb-6'>
                <h1 className="text-2xl font-bold flex items-center">
                    <small className="mr-4 text-gray-400 cursor-pointer" onClick={closeEdit}><FaArrowLeft /></small>
                    Déduction sur le salaire
                </h1>
                <div>
                    <button type='button' className='bg-orange-200 text-black rounded-full btn mx-2' onClick={closeEdit}>Annuler</button>
                    <button type="submit" className='bg-[--color-primary] text-white hover:bg-purple-500 rounded-full btn' disabled={loading}>
                        {loading ? <ClipLoader color='white' size={20} /> : 'Enregistrer'}
                    </button>
                    <ActionDropdown actions={dropdownActions} />
                </div>
            </div>

            {/* Section Informations générales */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="border-b border-gray-200 px-6 py-4">
                    <h3 className="text-lg font-semibold text-gray-800">Informations générales</h3>
                    <p className="text-sm text-gray-600 mt-1">Détails de base de la déduction</p>
                </div>
                <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label htmlFor="libelle" className="block text-sm font-medium text-gray-700">
                                Libellé *
                                {validationErrors.libelle && (
                                    <span className="text-red-500 text-xs ml-2">{validationErrors.libelle}</span>
                                )}
                            </label>
                            <input
                                type="text"
                                id="libelle"
                                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 ${validationErrors.libelle ? 'border-red-500' : ''}`}
                                placeholder="Libellé de la déduction"
                                value={formData.libelle}
                                onChange={handleFormChange}
                            />
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="montant" className="block text-sm font-medium text-gray-700">
                                Montant par défaut *
                                {validationErrors.montant && (
                                    <span className="text-red-500 text-xs ml-2">{validationErrors.montant}</span>
                                )}
                            </label>
                            <AmountInput
                                value={formData.montant}
                                onChange={handleMontantChange}
                                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 ${validationErrors.montant ? 'border-red-500' : ''}`}
                                placeholder="Montant de la déduction"
                            />
                            <p className="text-xs text-gray-500">
                                Ce montant sera utilisé par défaut pour tous les employés sauf si spécifié autrement.
                            </p>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea
                            id="description"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                            placeholder="Description de la déduction"
                            value={formData.description}
                            onChange={handleFormChange}
                            rows={3}
                        />
                    </div>
                </div>
            </div>            

            {/* Section Gestion des employés */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div 
                    className="border-b border-gray-200 px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                    onClick={() => setIsEmployeeSectionExpanded(!isEmployeeSectionExpanded)}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800">Assignation aux employés</h3>
                            <p className="text-sm text-gray-600 mt-1">Gestion des employés concernés par cette déduction</p>
                            {validationErrors.employeeLoans && (
                                <span className="text-red-500 text-xs">{validationErrors.employeeLoans}</span>
                            )}
                        </div>
                        <div className="text-gray-400">
                            {isEmployeeSectionExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </div>
                    </div>
                </div>
                {isEmployeeSectionExpanded && (
                    <div className="p-6">
                        <div className="rounded-lg border">
                            <table className="table w-full border-collapse">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th style={{ width: "35%" }} className="px-4 py-3 text-left text-sm font-medium text-gray-700">Employé</th>
                                        <th style={{ width: "25%" }} className="px-4 py-3 text-left text-sm font-medium text-gray-700">Montant</th>
                                        <th style={{ width: "20%" }} className="px-4 py-3 text-left text-sm font-medium text-gray-700">Début(MM/YYYY)</th>
                                        <th style={{ width: "20%" }} className="px-4 py-3 text-left text-sm font-medium text-gray-700">Fin(MM/YYYY)</th>
                                        <th style={{ width: "10%" }} className="px-4 py-3 text-center text-sm font-medium text-gray-700">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {employeeLoans.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                                                <div className="flex flex-col items-center">
                                                    <p className="mb-2">Aucun employé assigné</p>
                                                    <p className="text-sm">Cliquez sur "Ajouter un employé" ci-dessous pour commencer</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        employeeLoans.map((loan, index) => (
                                            <EmployeeDeductionRow
                                                key={index}
                                                loan={loan}
                                                index={index}
                                                onChange={handleEmployeeLoanChange}
                                                onRemove={removeEmployeeLoan}
                                                defaultAmount={formData.montant}                                        
                                            />
                                        ))
                                    )}
                                </tbody>
                            </table>

                            <div className="border-t bg-gray-50 p-4">
                                <button
                                    type="button"
                                    className="flex items-center text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-3 py-2 rounded-lg transition-colors duration-200"
                                    onClick={addEmployeeLoan}
                                >
                                    <FaPlus className="mr-2 text-xs" /> Ajouter un employé
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Section Configuration comptable */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div 
                    className="border-b border-gray-200 px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                    onClick={() => setIsAccountSectionExpanded(!isAccountSectionExpanded)}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800">Configuration comptable</h3>
                            <p className="text-sm text-gray-600 mt-1">Paramètres comptables et calculs</p>
                        </div>
                        <div className="text-gray-400">
                            {isAccountSectionExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </div>
                    </div>
                </div>
                {isAccountSectionExpanded && (
                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Compte de Débit
                                </label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                                    name="account_debit"
                                    value={formData.account_debit || ''}
                                    onChange={e => setFormData(prev => ({ ...prev, account_debit: e.target.value }))}
                                    placeholder="Compte de débit"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Compte de Crédit
                                </label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                                    name="account_credit"
                                    value={formData.account_credit || ''}
                                    onChange={e => setFormData(prev => ({ ...prev, account_credit: e.target.value }))}
                                    placeholder="Compte de crédit"
                                />
                            </div>
                        </div>
                        
                        {/* Séparateur visuel */}
                        <div className="border-t border-gray-200 pt-6">
                            <h4 className="text-md font-medium text-gray-800 mb-4">Options de calcul</h4>
                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                <div>
                                    <h5 className="font-medium text-gray-800">Non Computé dans le Net</h5>
                                    <p className="text-sm text-gray-600 mt-1">Cette déduction ne sera pas incluse dans le calcul du salaire net</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        name="not_computed_in_net"
                                        checked={!!formData.not_computed_in_net}
                                        onChange={e => setFormData(prev => ({ ...prev, not_computed_in_net: e.target.checked }))}
                                    />
                                    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                </label>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </form>

        <Modal
            isOpen={isArchiveModalOpen}
            onClose={() => setIsArchiveModalOpen(false)}
            title="Confirmation d'archivage"
            footer={
                <>
                    <button className="btn" onClick={() => setIsArchiveModalOpen(false)}>Annuler</button>
                    <button className="btn btn-error" onClick={handleArchive}>Confirmer</button>
                </>
            }
        >
            <p>Êtes-vous sûr de vouloir archiver cet élément ?</p>
        </Modal>
    </>
    );
};

export default DeductionForm;